'use client';

import { Loader2 } from 'lucide-react';
import * as React from 'react';

import { Input, type InputProps } from '../components/Input';
import { cn } from '../utils';

export type AutocompleteOption = {
  id: string;
  label: string;
  /** Rendered right-aligned on the row, e.g. a price. */
  trailingLabel?: React.ReactNode;
  /** Extra text matched against the query alongside `label`. */
  keywords?: string;
  /** Still listed, but can't be picked — keyboard navigation skips it. */
  disabled?: boolean;
};

export type AutocompleteInputProps = Omit<InputProps, 'value' | 'onChange' | 'onSelect' | 'ref'> & {
  value: string;
  onChange: (value: string) => void;
  options: AutocompleteOption[];
  onSelect: (option: AutocompleteOption) => void;
  isLoading?: boolean;
  maxResults?: number;
  minQueryLength?: number;
  emptyLabel?: React.ReactNode;
  containerClassName?: string;
  listClassName?: string;
};

export const AutocompleteInput = React.forwardRef<HTMLInputElement, AutocompleteInputProps>(
  (
    {
      value,
      onChange,
      options,
      onSelect,
      isLoading = false,
      maxResults = 50,
      minQueryLength = 1,
      emptyLabel,
      containerClassName,
      listClassName,
      className,
      disabled,
      onKeyDown,
      onBlur,
      onFocus,
      ...inputProps
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
    const listRef = React.useRef<HTMLDivElement>(null);
    const scrollHighlightIntoView = React.useRef(false);

    const query = value.trim().toLowerCase();

    const matches = React.useMemo(() => {
      if (query.length < minQueryLength) return [];
      return options
        .filter(
          (option) =>
            option.label.toLowerCase().includes(query) ||
            option.keywords?.toLowerCase().includes(query),
        )
        .slice(0, maxResults);
    }, [options, query, minQueryLength, maxResults]);

    React.useEffect(() => {
      setHighlightedIndex(-1);
    }, [query]);

    // Only keyboard navigation scrolls: hover fires as rows pass under a stationary cursor
    // during a wheel scroll, and scrolling them into view fights the user's own scroll.
    React.useEffect(() => {
      const shouldScroll = scrollHighlightIntoView.current;
      scrollHighlightIntoView.current = false;
      if (!shouldScroll || highlightedIndex < 0) return;
      const rows = listRef.current?.querySelectorAll('[data-autocomplete-option]');
      rows?.[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }, [highlightedIndex]);

    const showList = open && !disabled && query.length >= minQueryLength;
    const showEmpty = showList && matches.length === 0 && !isLoading && !!emptyLabel;

    const handleSelect = (option: AutocompleteOption) => {
      if (option.disabled) return;
      onSelect(option);
      setOpen(false);
      setHighlightedIndex(-1);
    };

    /** Next pickable row in `step` direction, or the current index when every row is disabled. */
    const nextEnabledIndex = (from: number, step: number) => {
      const wrap = (index: number) => ((index % matches.length) + matches.length) % matches.length;
      for (let offset = 1; offset <= matches.length; offset += 1) {
        const index = wrap(from + step * offset);
        if (!matches[index].disabled) return index;
      }
      return from;
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (showList && matches.length > 0) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          scrollHighlightIntoView.current = true;
          setHighlightedIndex((prev) => nextEnabledIndex(prev, 1));
          return;
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          scrollHighlightIntoView.current = true;
          setHighlightedIndex((prev) => nextEnabledIndex(prev < 0 ? 0 : prev, -1));
          return;
        }
        if (
          event.key === 'Enter' &&
          highlightedIndex >= 0 &&
          !matches[highlightedIndex]?.disabled
        ) {
          event.preventDefault();
          handleSelect(matches[highlightedIndex]);
          return;
        }
      }
      if (event.key === 'Escape' && open) {
        event.preventDefault();
        setOpen(false);
        setHighlightedIndex(-1);
        return;
      }
      onKeyDown?.(event);
    };

    return (
      <div className={cn('relative', containerClassName)}>
        <Input
          ref={ref}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
            setOpen(true);
          }}
          onFocus={(event) => {
            setOpen(true);
            onFocus?.(event);
          }}
          onBlur={(event) => {
            setOpen(false);
            setHighlightedIndex(-1);
            onBlur?.(event);
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          autoComplete="off"
          role="combobox"
          aria-expanded={showList}
          aria-autocomplete="list"
          className={cn(isLoading && 'pr-9', className)}
          {...inputProps}
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-gray-400" />
        )}
        {(showList && matches.length > 0) || showEmpty ? (
          <div
            ref={listRef}
            role="listbox"
            className={cn(
              'absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg',
              listClassName,
            )}
          >
            {showEmpty ? (
              <div className="px-3 py-2 text-sm text-gray-500">{emptyLabel}</div>
            ) : (
              matches.map((option, index) => (
                <button
                  key={option.id}
                  type="button"
                  role="option"
                  data-autocomplete-option
                  aria-selected={index === highlightedIndex}
                  aria-disabled={option.disabled}
                  disabled={option.disabled}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 px-3 py-2 text-left',
                    option.disabled
                      ? 'cursor-default bg-gray-50/60'
                      : 'cursor-pointer hover:bg-gray-50',
                    index === highlightedIndex && !option.disabled && 'bg-gray-100',
                  )}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => !option.disabled && setHighlightedIndex(index)}
                  onClick={() => handleSelect(option)}
                >
                  <span
                    className={cn(
                      'min-w-0 flex-1 break-words text-sm',
                      option.disabled ? 'text-gray-500' : 'text-gray-900',
                    )}
                  >
                    {option.label}
                  </span>
                  {option.trailingLabel !== undefined && (
                    <span className="shrink-0 text-sm font-semibold text-gray-900">
                      {option.trailingLabel}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>
    );
  },
);

AutocompleteInput.displayName = 'AutocompleteInput';
